import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType, TextInput} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import {MinWidthButtonWrapper, StyledWarning} from '../util/StyledElements';
import '../../static/css/modal.less';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {useTscTheme} from '../../context/themeProvider';
import {collectionsStore, updateCollection} from '../../context/collectionsProvider';
import {IWorkerState} from '../../model/workerState';
import {ICollectionModel} from '../../model/appModel';
import collectionApi from '../../service/collectionApi';
import Constants from '../../constants/Constants';
import StringUtil from '../../util/StringUtil';

interface IModalState extends IWorkerState {
   collectionName: string;
   errorMessage: string;
}

export const RenameCollectionModalBase: React.FC<IRenameCollectionModalProps & IStateMappedProps> = ({onClose, activeCollection}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [modalState, updateModalState] = useState<IModalState>({
      isWorking: false,
      hasError: false,
      collectionName: activeCollection.Name,
      errorMessage: ''
   });

   const updateCollectionName = (evt: React.ChangeEvent<HTMLInputElement>): void => {
      const updatedCollectionName = evt.target.value;

      updateModalState({
         ...modalState,
         collectionName: updatedCollectionName,
         hasError: updatedCollectionName.length > Constants.maxCollectionNameLength,
         errorMessage: updatedCollectionName.length > Constants.maxCollectionNameLength ? t('collection.modal.rename.errorNameTooLong', {maxLength: Constants.maxCollectionNameLength}) : ''
      });
   };

   const renameCollection = async () => {
      const trimmedCollectionName = modalState.collectionName.trim();
      await collectionApi.renameCollection(activeCollection.Id, trimmedCollectionName);
      updateCollection({...activeCollection, Name: trimmedCollectionName});
      await onClose();
   };

   return (
      <BasicModalPortal
         visible={true}
         title={t('collection.modal.rename.title')}
         width={cssConstants.defaultModalWidth}
         onClose={modalState.isWorking ? (): void => null : onClose}
         themeName={theme}
         isLoading={modalState.isWorking}
      >
         <div className="modal-body">
            <div className="form-body themeable-section">
               <div className="form-field-group">
                  {modalState.hasError && (
                     <StyledWarning>
                        {StringUtil.isNullOrWhiteSpace(modalState.errorMessage) ? t('general.errorText') : modalState.errorMessage}
                     </StyledWarning>
                  )}
                  <label>
                     {t('collection.modal.rename.inputLabel')}
                     <TextInput
                        value={modalState.collectionName}
                        onChange={updateCollectionName}
                        themeName={theme}
                        ariaLabel={t('collection.modal.rename.inputLabel')}
                        readOnly={false}
                        testId="rename-collection-link-input"
                        disabled={modalState.isWorking}
                     />
                  </label>
               </div>
               <MinWidthButtonWrapper className="button-group">
                  <ControlButton
                     label={t('general.cancel')}
                     buttonType={TscButtonType.secondary}
                     disabled={modalState.isWorking}
                     themeName={theme}
                     onClick={modalState.isWorking ? (): void => null : onClose}
                     testId="rename-collection-cancel-button"
                  />
                  <ControlButton
                     label={t('conversations.actions.rename')}
                     buttonType={TscButtonType.primary}
                     disabled={StringUtil.isNullOrWhiteSpace(modalState.collectionName) || modalState.hasError}
                     themeName={theme}
                     onClick={renameCollection}
                     testId="rename-collection-submit-button"
                  />
               </MinWidthButtonWrapper>
            </div>
         </div>
      </BasicModalPortal>
   );
};

export interface IRenameCollectionModalProps {
   onClose(): void | Promise<void>;
}

export interface IStateMappedProps {
   activeCollection: ICollectionModel;
}

const mapStatesToProps = (
   {onClose}: IRenameCollectionModalProps,
   {activeCollection}: Partial<IStateMappedProps>
): IStateMappedProps & IRenameCollectionModalProps => ({onClose, activeCollection});

export default withMemoizedContexts(mapStatesToProps, collectionsStore)(RenameCollectionModalBase);
